import { buildUri, CLOUDFRONT_URI, DEFAULT_LOCALE_CODE } from '../../const';
import { isRelativePath } from '../../updated/utils';
import { CourseUtil } from './CourseUtil';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';

function setIfNotUndefined( object, path, value ) {
    if ( value !== undefined ) {
        set( object, path, value );
    }
}

export class CurriculumUtil {

    /**
     * @typedef CurriculumInputGraphQL
     * @property { string } id - The ID of the curriculum.
     * @property { boolean } isPublic - Whether or not the curriculum is public.
     * @property { boolean } isSequential - Whether or not the curriculum is sequential.
     * @property { { locale: string, name: string, description: string }[] } locales - The locale data.
     * @property { string[] } courseIds - The list of learning object IDs to include in the curriculum.
     * @property { string[] } tags - The tags associated with the curriculum.
     * @property { string } thumbnailImageId - The ID of the thumbnail image.
     * @property { string } heroImageId - The ID of the hero image.
     * @property { string } bannerImageId - The ID of the banner image.
     * @property { object } metadata - The metadata.
     */

    /**
     * @typedef CurriculumInputLocal
     * ...
     */

    /**
     * @typedef { CurriculumInputGraphQL & CurriculumInputLocal } CurriculumInput
     */

    /**
     * Builds the GraphQL curriculum input given the response of an already existing
     * curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { CurriculumInputGraphQL }
     */
    static buildPatchInputFromCurriculum( curriculumData ) {
        const dataCopy = cloneDeep( curriculumData );
        return {
            id: this.getId( dataCopy ),
            isPublic: this.getIsPublic( dataCopy ),
            isSequential: this.getIsSequential( dataCopy ),
            locales: this.getCmxLocales( dataCopy ),
            courseIds: this.getCourseIds( dataCopy ),
            tags: this.getTags( dataCopy, { hideJsonTags: false } ),
            thumbnailImageId: this.getThumbnailImageId( dataCopy ),
            heroImageId: this.getHeroImageId( dataCopy ),
            bannerImageId: this.getBannerImageId( dataCopy ),
            metadata: this.getMetadata( dataCopy ),
        };
    }

    /**
     * Transforms provided input into an input structure supported by GQL.
     * 
     * @param { object } previousData - The previous curriculum data to modify.
     * @param { CurriculumInput } input - The data to store.
     * @returns { object }
     */
    static transformPatchInput( previousData, input ) {
        const newInput = this.buildPatchInputFromCurriculum( previousData );

        // Copy over the basic properties that don't need any special handling.
        setIfNotUndefined( newInput, 'id', input?.id );
        setIfNotUndefined( newInput, 'isPublic', input?.isPublic );
        setIfNotUndefined( newInput, 'isSequential', input?.isSequential );
        setIfNotUndefined( newInput, 'courseIds', input?.courseIds );
        setIfNotUndefined( newInput, 'tags', input?.tags );
        setIfNotUndefined( newInput, 'thumbnailImageId', input?.thumbnailImageId );
        setIfNotUndefined( newInput, 'heroImageId', input?.heroImageId );
        setIfNotUndefined( newInput, 'bannerImageId', input?.bannerImageId );
        setIfNotUndefined( newInput, 'metadata', input?.metadata );
        setIfNotUndefined( newInput, 'locales', input?.locales );
        setIfNotUndefined( newInput, 'isFeatured', input?.isFeatured );
        setIfNotUndefined( newInput, 'weight', input?.weight );

        return newInput;
    }

    /**
     * Retrieves the ID from the provided curriculum data.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string }
     */
    static getId( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getId( curriculumData );
    }

    /**
     * Retrieves the name from a curriculum object in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code
     * of `en-US` will be used.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { string } localeCode - The language to retrieve the data in.
     * @returns { string }
     */
    static getName( curriculumData, localeCode = DEFAULT_LOCALE_CODE ) {
        // Same logic as courses.
        return CourseUtil.getName( curriculumData, localeCode );
    }

    /**
     * Retrieves the description from a curriculum object in the language represented
     * by the provided locale code. If no locale code is provided, a default locale
     * code of `en-US` will be used.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { string } localeCode - The language to retrieve the data in.
     * @returns { string }
     */
    static getDescription( curriculumData, localeCode = DEFAULT_LOCALE_CODE ) {
        // Same logic as courses.
        return CourseUtil.getDescription( curriculumData, localeCode );
    }

    /**
     * Retrieves the list of all locale data for the provided curriculum. This includes
     * locales and translations stored in CMX as well as any additional languages
     * potentially stored in the curriculum's metadata.
     * 
     * Once CMX supports all language/locale data that we need, we should be able to
     * get by only using the CMX locale data and not the metadata, but it technically
     * doesn't hurt to leave the support for the metadata translations.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { { locale: string, name: string, description: string }[] }
     */
    static getLocales( curriculumData ) { 
        // Same logic as courses.
        return CourseUtil.getLocales( curriculumData );
    }

    /**
     * Retrieves the list of locale data for the provided curriculum. These are only
     * the locales that are officially supported by CMX and are stored in CMX.
     * 
     * @param { object[] } curriculumData - The data to read.
     * @returns { { locale: string, name: string, description: string }[] }
     */
    static getCmxLocales( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getCmxLocales( curriculumData );
    }

    /**
     * Returns whether or not the provided curriculum is considered to be active.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { boolean }
     */
    static getIsActive( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getIsActive( curriculumData );
    }

    /**
     * Returns whether or not the provided curriculum is considered to be public.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { boolean }
     */
    static getIsPublic( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getIsPublic( curriculumData );
    }

    /**
     * Returns whether or not the curriculum courses are required to be in sequential
     * order.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { boolean }
     */
    static getIsSequential( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getIsSequential( curriculumData );
    }

    /**
     * Retrieves the date the curriculum was created in milleseconds.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { number }
     */
    static getDateOfCreation( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getDateOfCreation( curriculumData );
    }

    /**
     * Retrieves the date the curriculum was last updated in milleseconds.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { number }
     */
    static getDateOfLastUpdate( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getDateOfLastUpdate( curriculumData );
    }

    /**
     * Retrieves the tags from the curriculum. Note that curriculum tags are not
     * currently translatable.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { object } options - Options that change what data is retrieved.
     * @returns { string[] }
     */
    static getTags( curriculumData, { hideJsonTags = true } = {} ) {
        // Same logic as courses.
        return CourseUtil.getTags( curriculumData, { hideJsonTags } );
    }

    /**
     * Retrieves the tagIds from the curriculum 
     * 
     * @param { object } curriculumData - The data to read.
      * @returns { string[] }
     */
    static getTagIds( curriculumData  ) {
        return curriculumData?.node?.tagIds;
    }

    /**
     * Retrieves the thumbnail image for the curriculum.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getThumbnailImage( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getThumbnailImage( curriculumData );
    }

    /**
     * Retrieves the thumbnail image ID for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getThumbnailImageId( curriculumData ) {
        return this.getThumbnailImage( curriculumData )?.id;
    }

    /**
     * Retrieves the URL of the thumbnail image for the course.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getThumbnailImageUrl( curriculumData, { forceAbsolutePath = true } = {} ) {
        const path = this.getThumbnailImage( curriculumData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_URI, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the hero image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getHeroImage( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getHeroImage( curriculumData );
    }

    /**
     * Retrieves the hero image ID for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getHeroImageId( curriculumData ) {
        return this.getHeroImage( curriculumData )?.id;
    }

    /**
     * Retrieves the URL of the hero image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getHeroImageUrl( curriculumData, { forceAbsolutePath = true } = {} ) {
        const path = this.getHeroImage( curriculumData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_URI, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the banner image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getBannerImage( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getBannerImage( curriculumData );
    }

    /**
     * Retrieves the banner image ID for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getBannerImageId( curriculumData ) {
        return this.getBannerImage( curriculumData )?.id;
    }

    /**
     * Retrieves the URL of the banner image for the curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getBannerImageUrl( curriculumData, { forceAbsolutePath = true } = {} ) {
        const path = this.getBannerImage( curriculumData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_URI, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the number of courses that exist inside the course.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { number }
     */
    static getCourseCount( curriculumData ) {
        let courseCount = 0;

        // Attempt to rely on the `totalCount` property to get the total number of courses
        // in the curriculum, but if that is not available, attempt to get the total number
        // using the actual course data array if possible.
        const totalCount = curriculumData?.node?.courses?.totalCount;
        if ( totalCount !== undefined ) {
            courseCount = totalCount;
        } else {
            const edgesLength = curriculumData?.node?.courses?.edges?.length;
            if ( edgesLength !== undefined ) {
                courseCount = edgesLength;
            }
        }

        return courseCount;
    }

    /**
     * Retrieves the IDs of the courses that exist inside the course.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string[] }
     */
    static getCourseIds( curriculumData ) {
        const objectEdges = curriculumData?.node?.courses?.edges || [];
        return objectEdges.map( ( courseData ) => CourseUtil.getId( courseData ) );
    }

    /**
     * Retrieves the metadata from inside of the provided curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { object }
     */
    static getMetadata( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getMetadata( curriculumData );
    }

    /**
     * Retrieves the company relationships from inside of the provided curriculum.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string[] } - company relationship IDs
     */
    static getCompanyRelationshipIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getCompanyRelationshipIds( curriculumData );
    }

    /**
     * Retrieves the brand IDs that the curriculum is a part of.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string[] }
     */
    static getBrandIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getBrandIds( curriculumData );
    }

    /**
     * Retrieves the product IDs that the curriculum is a part of.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string[] }
     */
    static getProductIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getProductIds( curriculumData );
    }

    /**
     * Retrieves the segment IDs that the curriculum is a part of.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string[] }
     */
    static getSegmentIds( curriculumData ) {
        // Same logic as courses.
        return CourseUtil.getSegmentIds( curriculumData );
    }

    /**
     * Retrieves the category IDs that the curriculum is a part of.
     * 
     * @param { object } curriculumData - The data to read.
     * @returns { string[] }
     */
    static getCategoryIds( curriculumData ) {
        return CourseUtil.getCategoryIds( curriculumData );
    }

    static getIsFeatured( curriculumData  ) {
        return curriculumData?.node?.isFeatured === true || false;
    }

    static getWeight( curriculumData  ) {
        return curriculumData?.node?.weight || 0;
    }
}
